import React from 'react';

/**
 * The job card header: the order line across the top, then the job's own facts
 * in a dense band of label/value pairs — four columns of them, all visible at
 * once, exactly as the printed card reads.
 *
 * Most of these are written by hand as the job firms up, so they are inputs
 * rather than labels; they edit the job card's specifications and save with the
 * rest of the card. The few that are facts of the order — the job number, the
 * date it was raised, whether it is new or a repeat — stay read-only, because
 * changing them here would mean the card disagreed with the order it came from.
 */

const fmt = (value) => (value === null || value === undefined ? '' : String(value));

const date = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN') : '');

// label -> the job card specification it writes into
const EDITABLE = [
  ['Job Color', 'job_color', 'text'],
  ['Designer', 'designer', 'text'],
  ['Plate', 'plate_no', 'text'],
  ['Paper', 'paper_quality', 'text'],
  ['Full Sht.Qty', 'po_sheets', 'number'],
  ['Paper Size', 'full_sheet_size', 'text'],
  ['Die No.', 'die_no', 'text'],
  ['Plan Sheets', 'plan_sheets', 'number'],
  ['Division', 'division', 'text'],
  ['PaperCut Size', 'cut_size', 'text'],
  ['GSM', 'gsm', 'number'],
  ['Ups', 'ups_per_sheet', 'number'],
];

function Cell({ label, children }) {
  return (
    <div className="flex items-stretch text-xs">
      <span className="bg-brand-blue text-white px-2 py-1 font-medium whitespace-nowrap min-w-[6.5rem]">
        {label}
      </span>
      <span className="flex-1 border border-l-0 border-slate-300 bg-white min-w-0">
        {children}
      </span>
    </div>
  );
}

function ReadOnly({ label, value }) {
  return (
    <Cell label={label}>
      <span className="block px-2 py-1 truncate tabular-nums text-slate-600">{fmt(value)}</span>
    </Cell>
  );
}

function Editable({ label, name, type, specs, onChange, disabled }) {
  if (disabled) return <ReadOnly label={label} value={specs?.[name]} />;
  // A numeric field stores a number, the same as the spec grid does. Typing GSM
  // here used to store "300" and typing it there stored 300, so the same field
  // held two types depending on which control last touched it — and anything
  // doing arithmetic on it (sheet weight, imposition) got a string half the time.
  const write = (raw) => onChange(name, type === 'number' && raw !== '' ? Number(raw) : raw);
  return (
    <Cell label={label}>
      <input
        type={type}
        value={specs?.[name] ?? ''}
        onChange={e => write(e.target.value)}
        aria-label={label}
        data-testid={`header-${name}`}
        className="w-full px-2 py-1 tabular-nums bg-transparent focus:outline-none focus:bg-blue-50 focus:ring-1 focus:ring-brand-blue focus:ring-inset"
      />
    </Cell>
  );
}

export default function JobHeaderBand({
  info = {}, quantity, orderId, specs = {}, onChange, canEdit = false,
}) {
  const editable = (label) => {
    const found = EDITABLE.find(([l]) => l === label);
    const [, name, type] = found;
    return (
      <Editable key={label} label={label} name={name} type={type}
        specs={specs} onChange={onChange} disabled={!canEdit} />
    );
  };

  return (
    <div className="space-y-2">
      {/* the order line this job card came from */}
      <div className="overflow-x-auto border border-slate-300">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-brand-blue text-white">
              {['Sr.', 'Order No.', 'Party', 'P.O.No.', 'P.O. Date', 'Product',
                'Size', 'Balance PO Qty', 'Plan Qty.', 'Ups'].map(h => (
                <th key={h} className="px-2 py-1.5 font-semibold border-r border-blue-400 last:border-r-0 whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-slate-200">
              <td className="px-2 py-1 text-center border-r border-slate-200">1</td>
              <td className="px-2 py-1 border-r border-slate-200 font-mono text-[11px]">{orderId}</td>
              <td className="px-2 py-1 border-r border-slate-200">{fmt(info.party)}</td>
              <td className="px-2 py-1 border-r border-slate-200">{fmt(info.po_no)}</td>
              <td className="px-2 py-1 border-r border-slate-200">{date(info.po_date)}</td>
              <td className="px-2 py-1 border-r border-slate-200">{fmt(info.job_name)}</td>
              <td className="px-2 py-1 border-r border-slate-200 tabular-nums">{fmt(info.size)}</td>
              <td className="px-2 py-1 text-right border-r border-slate-200 tabular-nums">
                {Number(quantity || 0).toLocaleString()}
              </td>
              <td className="px-2 py-1 text-right border-r border-slate-200 tabular-nums">
                {info.plan_qty ? Number(info.plan_qty).toLocaleString() : ''}
              </td>
              <td className="px-2 py-1 text-right tabular-nums">{fmt(specs.ups_per_sheet)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="grid gap-x-3 gap-y-1 md:grid-cols-2 xl:grid-cols-4">
        <ReadOnly label="Job No." value={info.job_no} />
        <ReadOnly label="Job Date" value={date(info.job_date)} />
        {editable('Job Color')}
        <ReadOnly label="Job Name" value={info.job_name} />

        {editable('Designer')}
        <ReadOnly label="Job Type" value={String(info.job_type || '').toUpperCase()} />
        {editable('Plate')}
        {editable('Paper')}

        {editable('Full Sht.Qty')}
        {editable('Paper Size')}
        {editable('Die No.')}
        {editable('Plan Sheets')}

        {editable('Division')}
        {editable('PaperCut Size')}
        {editable('GSM')}
        {editable('Ups')}
      </div>
    </div>
  );
}
